import React, { useRef, useEffect, useState } from 'react';
import { ArrowRight, Upload, PlusCircle, AlertTriangle, TrendingUp, BarChart, Play, Heart } from 'lucide-react';

const Hero = () => {
  const heroRef = useRef<HTMLDivElement>(null);
  const [videoModal, setVideoModal] = useState(false);
  const [investmentType, setInvestmentType] = useState("Private Equity");
  const [isTransitioning, setIsTransitioning] = useState(false);
  const currentIndexRef = useRef(0);
  const [showInsights, setShowInsights] = useState(false);

  useEffect(() => {
    const investmentTypes = ["Private Equity", "Private Credit", "Infrastructure", "Venture Capital"];
    const intervalId = setInterval(() => {
      // Start fade out
      setIsTransitioning(true);
      
      // After fade out completes, change the text and start fade in
      setTimeout(() => {
        currentIndexRef.current = (currentIndexRef.current + 1) % investmentTypes.length;
        setInvestmentType(investmentTypes[currentIndexRef.current]);
        setIsTransitioning(false);
      }, 500); // Half a second for fade out
      
    }, 3000); // 3 seconds total - longer transition for better readability

    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!heroRef.current) return;
      
      const { clientX, clientY } = e;
      const rect = heroRef.current.getBoundingClientRect();
      
      const x = (clientX - rect.left) / rect.width;
      const y = (clientY - rect.top) / rect.height;
      
      const moveX = (x - 0.5) * 20;
      const moveY = (y - 0.5) * 20;
      
      heroRef.current.style.setProperty('--move-x', `${moveX}px`);
      heroRef.current.style.setProperty('--move-y', `${moveY}px`);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  return (
    <div 
      ref={heroRef}
      className="relative min-h-screen flex items-center pt-24 pb-20 overflow-hidden hero-gradient"
      style={{
        '--move-x': '0px',
        '--move-y': '0px',
      } as React.CSSProperties}
    >
      {/* Background Effects */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute w-full h-full data-grid opacity-40"></div>
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full bg-gradient-radial from-blue-400/10 to-transparent animate-pulse-slow"
          style={{
            transform: 'translate(calc(-50% + var(--move-x) * 0.5), calc(-50% + var(--move-y) * 0.5))',
          }}
        ></div>
        <div 
          className="absolute top-1/4 right-1/4 w-64 h-64 rounded-full bg-purple-300/10 blur-3xl animate-float"
        ></div>
        <div 
          className="absolute bottom-1/4 left-1/4 w-64 h-64 rounded-full bg-blue-300/10 blur-3xl animate-float"
          style={{ animationDelay: '2s' }}
        ></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-3xl mx-auto text-center">
          <div className="inline-block px-3 py-1 mb-6 bg-blue-100 text-blue-600 rounded-full font-medium text-sm animate-fade-in">
            Next-generation AI Analytics Platform
          </div>
          
          <h1 className="heading-xl mb-6 animate-fade-in animate-delay-100">
            Turn <span className={`text-gradient transition-opacity duration-500 ${isTransitioning ? 'opacity-0' : 'opacity-100'}`}>{investmentType}</span> Data Into <span className="text-gradient">Actionable Insights</span>—Automatically.
          </h1>
          
          <p className="text-gray-600 text-lg mb-8 max-w-2xl mx-auto animate-fade-in animate-delay-200">
            From quarterly reports to real-time dashboards—Portco AI extracts, analyzes, and visualizes fund performance with AI-driven accuracy.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-fade-in animate-delay-300">
            <a
              href="#demo"
              className="btn-primary rounded-full flex items-center group w-full sm:w-auto"
            >
              <span>Request a Demo</span>
              <ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />
            </a>
            <a
              href="#features"
              className="btn-secondary rounded-full w-full sm:w-auto"
            >
              Learn More
            </a>
          </div>

          {/* Added "Made with love" message */}
          <p className="flex items-center justify-center text-gray-500 text-xs mt-8 animate-fade-in animate-delay-350 space-x-1">
            <span>Made with</span>
            <Heart className="w-3 h-3 text-red-500 fill-current" />
            <span>in Toronto, Canada</span>
          </p>
        </div>
        
        {/* Added Portco AI in 3 minutes section */}
        <div className="mt-12 max-w-3xl mx-auto text-center animate-fade-in animate-delay-350">
          <h2 className="text-2xl font-semibold mb-3">Portco AI in 3 minutes</h2>
          <p className="text-gray-600 mb-8">
            Watch how our platform transforms complex financial documents into actionable insights in minutes, not days.
          </p>
          
          <div className="max-w-3xl mx-auto">
            <div className="relative rounded-xl overflow-hidden shadow-xl border border-gray-100">
              <div style={{ position: "relative", paddingBottom: "54.21686746987952%", height: 0 }}>
                <iframe 
                  src="https://www.loom.com/embed/62ccc496f53a4d58b404130fdaa5769d?sid=661e5519-980b-48a8-9521-1eab40543f13" 
                  frameBorder="0" 
                  allowFullScreen 
                  style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%" }}
                ></iframe>
              </div>
            </div>
          </div>
        </div>
        
        {/* Dashboard Preview */}
        <div className="mt-16 max-w-6xl mx-auto animate-fade-in animate-delay-400">
          <div className="text-center mb-12">
            <h2 className="heading-lg mb-4">Transparent Pricing for Every Fund Size</h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Contact us for a custom solution tailored to your specific requirements.
            </p>
            <a
              href="#contact"
              className="btn-primary rounded-full inline-flex items-center mt-8 group"
            >
              <span>Contact Us For Pricing</span>
              <ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />
            </a>
          </div>
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-t from-background to-transparent z-10 h-20 bottom-0 top-auto"></div>
            <div className="glass-card rounded-xl overflow-hidden shadow-2xl border border-blue-100 transform transition-transform hover:scale-[1.01] duration-700 shadow-[0_0_50px_-12px_rgba(59,130,246,0.25)]">
              <img 
                src="/lovable-uploads/portfolio_overview_portco.png" 
                alt="Portco AI Dashboard Overview" 
                className="w-full h-auto"
              />
            </div>
          </div>
        </div>
      </div>
      
      {/* Video Modal */}
      {videoModal && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4">
          <div className="relative w-full max-w-4xl">
            <button 
              className="absolute -top-12 right-0 text-white hover:text-blue-400 transition-colors"
              onClick={() => setVideoModal(false)}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <div className="aspect-video bg-black rounded-lg overflow-hidden">
              <div style={{ position: "relative", paddingBottom: "54.21686746987952%", height: 0 }}>
                <iframe 
                  src="https://www.loom.com/embed/62ccc496f53a4d58b404130fdaa5769d?sid=661e5519-980b-48a8-9521-1eab40543f13" 
                  frameBorder="0" 
                  allowFullScreen 
                  style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%" }}
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Hero;
